import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, User, Mail, Target, FileText, CheckCircle2, XCircle, AlertCircle, Maximize2, Info } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Separator } from '@/shared/components/ui/separator'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useCampania } from '@/modules/multichannel/hooks/useCampanias'

export function CampaniaDetalle() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const { data: campania, isLoading, error } = useCampania(Number(id))

  const getEstadoBadge = (estado: string) => {
    const estadoLower = estado.toLowerCase()
    if (estadoLower.includes('enviado') || estadoLower.includes('aprobado')) {
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{estado}</Badge>
    }
    if (estadoLower.includes('programado')) {
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">{estado}</Badge>
    }
    if (estadoLower.includes('pendiente')) {
      return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">{estado}</Badge>
    }
    if (estadoLower.includes('borrador')) {
      return <Badge variant="outline">{estado}</Badge>
    }
    return <Badge variant="secondary">{estado}</Badge>
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return new Intl.DateTimeFormat('es-EC', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date)
  }

  const formatTime = (timeString: string | null) => {
    if (!timeString) return '-'
    return timeString.slice(0, 5) // HH:MM
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-[400px]" />
            <Skeleton className="h-4 w-[300px]" />
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-[400px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    )
  }

  if (error || !campania) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <XCircle className="h-16 w-16 text-destructive mb-4" />
        <h2 className="text-2xl font-bold mb-2">Campaña no encontrada</h2>
        <p className="text-muted-foreground mb-6">
          No se pudo cargar la información de esta campaña
        </p>
        <Button onClick={() => navigate('/multichannel/seguimiento')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver al listado
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/multichannel/seguimiento')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            {campania.nombre_campania}
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground text-sm animate-in slide-in-from-left-2 duration-200">
              {campania.marca_principal_nombre || campania.marca} - ID: {campania.id}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/multichannel/campania/${id}/editar`)}>
            Editar
          </Button>
          <Button className="bg-black hover:bg-gray-800 text-white">Enviar Prueba</Button>
        </div>
      </div>

      {/* Estados principales */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Contenido
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getEstadoBadge(campania.estado_contenido)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Envío
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getEstadoBadge(campania.estado_envio)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Reporte
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getEstadoBadge(campania.estado_reporte)}
          </CardContent>
        </Card>
        {campania.requiere_aprobacion && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Aprobación
              </CardTitle>
            </CardHeader>
            <CardContent>
              {campania.estado_aprobacion ? getEstadoBadge(campania.estado_aprobacion) : '-'}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Contenido principal */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Información General */}
        <Card>
          <CardHeader>
            <CardTitle>Información General</CardTitle>
            <CardDescription>Detalles básicos de la campaña</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <User className="h-4 w-4 text-muted-foreground" />
                Laboratorio
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                {campania.marca_principal_nombre || campania.marca}
              </p>
            </div>

            {campania.marca_secundaria && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  Marca Secundaria
                </div>
                <p className="text-sm text-muted-foreground pl-6">
                  {campania.marca_secundaria}
                </p>
              </div>
            )}

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Remitente
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                {campania.remitente}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Target className="h-4 w-4 text-muted-foreground" />
                Target
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                {campania.target}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                Base Segmentada
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                {campania.base_segmentada}
              </p>
            </div>

            {campania.asesor_tonal_nombre && (
              <>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <User className="h-4 w-4 text-muted-foreground" />
                    Asesor Tonal
                  </div>
                  <p className="text-sm text-muted-foreground pl-6">
                    {campania.asesor_tonal_nombre}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Configuración */}
        <Card>
          <CardHeader>
            <CardTitle>Configuración</CardTitle>
            <CardDescription>Tipo y formato de la campaña</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="text-sm font-medium">Tipo</div>
                <Badge variant="outline" className="font-normal">
                  {campania.tipo}
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">Formato</div>
                <Badge variant="outline" className="font-normal">
                  {campania.formato}
                </Badge>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="text-sm font-medium">Tipo de Envío</div>
              <Badge variant="secondary">{campania.tipo_envio}</Badge>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {campania.requiere_aprobacion ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">Requiere Aprobación</span>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Fecha de Publicación
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                {formatDate(campania.fecha_publicacion)}
              </p>
            </div>

            {campania.hora && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  Hora
                </div>
                <p className="text-sm text-muted-foreground pl-6">
                  {formatTime(campania.hora)}
                </p>
              </div>
            )}

            {campania.dias_desde_envio > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium">Días desde envío</div>
                <Badge variant="outline">{campania.dias_desde_envio} días</Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contenido del Email */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Contenido del Email</CardTitle>
            <CardDescription>Asunto, preencabezado y contenido</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {campania.asunto && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  Asunto
                </div>
                <p className="text-sm bg-muted p-3 rounded-md">
                  {campania.asunto}
                </p>
              </div>
            )}

            {campania.pre_encabezado && (
              <div className="space-y-2">
                <div className="text-sm font-medium">Pre-encabezado</div>
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                  {campania.pre_encabezado}
                </p>
              </div>
            )}

            {campania.titulo_envio && (
              <div className="space-y-2">
                <div className="text-sm font-medium">Título del Envío</div>
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                  {campania.titulo_envio}
                </p>
              </div>
            )}

            <Separator />

            {campania.archivo_url && (
              <div className="space-y-2">
                <div className="text-sm font-medium">Archivo Adjunto</div>
                <a
                  href={campania.archivo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Ver archivo
                </a>
              </div>
            )}

            {campania.contenido_html && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Contenido HTML</div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsPreviewOpen(true)}
                  >
                    <Maximize2 className="mr-2 h-3 w-3" />
                    Vista previa
                  </Button>
                </div>
                <div className="bg-muted p-4 rounded-md max-h-[300px] overflow-auto">
                  <pre className="text-xs whitespace-pre-wrap break-words">
                    {campania.contenido_html}
                  </pre>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notas adicionales */}
        {campania.notas && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Notas</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {campania.notas}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Reporte */}
        {campania.reporte && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Información del Reporte</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  {campania.reporte}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal de Vista Previa */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle>Vista Previa del Email</DialogTitle>
            <DialogDescription>
              Previsualización del contenido HTML de la campaña
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-6">
            <div className="border rounded-lg overflow-hidden bg-white">
              <iframe
                srcDoc={campania?.contenido_html || ''}
                className="w-full h-[70vh]"
                title="Vista previa del email"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  )
}
